import { store } from "redux/store";
import axios from "axios";

// utilities
import { calcDistance } from "common/calcDistance";
import { getAirportById } from "hooks/useAirport";

// classes
import Slot from "classes/slot";
import Revenue from "./revenue";
import FlightPlan from "./flightPlan";

// api
import api from "api/api";

/** A class representing an individual flight.*/
class Flight {
	#id;
	#slotId;
	#origin;
	#color;
	#distance;
	#number;

	/**
	 * Create a flight.
	 * @param {string} _slotId - A uuid of the slot that the flight is utilizing, i.e. a slot at the destination airport.
	 * @param {number} _origin - The id of the flight origin, i.e the hub utilized for the flight.
	 * @param {string} _color - The color of the flight.
	 * @param {number} sequence - The sequence of the flight.
	 * @param {Object} _options - Additional options for the flight.
	 * @param {string} _options.id - The id of the flight.
	 * @param {number} _options.number - The number of the flight.
	 * @param {Array<Object>} _options.prices - The prices of the flight.
	 * @param {string} _options.planId - The id of the flight plan.
	 * @param {Array<Object>} _options.otherFlights - Other flights associated with the flight plan.
	 */
	constructor(_slotId, _origin, _color, sequence, _options = {}) {
		const planFl = Flight.getByIds(FlightPlan.get(_options.planId)?.flights); // All saved flights contained only in the flight plan that is being edited
		const deletedFlights = planFl?.filter(item => !_options.otherFlights?.includes(item)); // Flights that are still saved, but were deleted in the creator

		this.#id = _options.id || null;
		this.#slotId = _slotId;
		this.#origin = _origin;
		this.#color = _color;
		this.#distance = Slot.get(this.#slotId)?.distance || this.calcDistance();
		this.#number = _options.number || getNumber.call(this, deletedFlights);
		this.prices = _options.prices || Revenue.generatePrices(this.#distance);
		this.sequence = sequence;

		function getNumber(deletedFlights) {
			const airlineInfo = store.getState().airlineInfo;
			const splittedName = airlineInfo.info.name.split(" ");
			const deletedFlightsIds = deletedFlights.map(f => f.id);

			let lastNumber = getLastNumber.call(this, deletedFlights);
			const notSavedNumbers = _options.otherFlights.map(f => f.number); // This may also contain saved flights

			do {
				var newNumber = lastNumber + 1;
				lastNumber++;
			} while (Flight.exists(newNumber, [this.id, ...deletedFlightsIds], notSavedNumbers));

			const withLetters = `${getPrefix.call(this, splittedName)} ${("000" + newNumber).substring(
				("000" + newNumber).length - 3
			)}`;
			return withLetters;
		}

		function getPrefix(splittedName) {
			if (splittedName.length === 1) {
				if (splittedName[0][1]) return splittedName[0][0].concat(splittedName[0][1]).toUpperCase();
				return splittedName[0][0];
			}

			return splittedName[0][0].concat(splittedName[splittedName.length - 1][0]).toUpperCase();
		}

		function getLastNumber(deletedFlights) {
			var allFlightNumbers = Flight.getAll().map(f => f.number);
			const deletedFlightsNums = deletedFlights.map(f => f.number);

			if (Array.isArray(_options.otherFlights)) {
				allFlightNumbers.push(..._options.otherFlights.map(f => f.number));
			}

			if (Array.isArray(deletedFlightsNums)) {
				allFlightNumbers = allFlightNumbers.filter(fn => !deletedFlightsNums.includes(fn));
			}

			if (allFlightNumbers.length === 0 || allFlightNumbers.includes(null) || allFlightNumbers.includes(undefined)) {
				return 0;
			}

			const asNumbers = allFlightNumbers.map(n => +n.replace(/\D/g, "")).sort((a, b) => a - b);
			const missingIndex = asNumbers.findIndex((v, i, a) => (i === 0 && v !== 1 ? true : v < a[i + 1] - 1));
			const index = missingIndex === -1 ? asNumbers.length - 1 : asNumbers[missingIndex];
			return asNumbers[index];
		}
	}

	get id() {
		return this.#id;
	}

	get slotId() {
		return this.#slotId;
	}

	get origin() {
		return this.#origin;
	}

	get color() {
		return this.#color;
	}

	get distance() {
		return this.#distance;
	}

	get number() {
		return this.#number;
	}

	calcDistance() {
		if (!this.#slotId) {
			throw new Error(`Flight #${this.#id} doesn't have a slot assigned!`);
		}

		const airportList = store.getState().airports;
		let originAirport;
		let slot;

		if (!(originAirport = getAirportById(airportList, this.#origin))) {
			throw new Error(`Flight #${this.#id} doesn't have an assigned origin or it doesn't exist!`);
		}

		if (!(slot = Slot.get(this.#slotId))) {
			throw new Error(`Flight #${this.#id} has an assigned slot, but it doesn't exist!`);
		}

		const { latitude_deg: lat1, longitude_deg: lng1 } = originAirport;
		const { latitude_deg: lat2, longitude_deg: lng2 } = getAirportById(airportList, slot.destination);
		return calcDistance(lat1, lng1, lat2, lng2);
	}

	setColor(_color) {
		this.#color = _color;
	}

	/**
	 * A method that returns origin and destination airports of the flight.
	 * @param {Number} aircraftLocation An id of aircraft current location. Optional.
	 * @return {Array<Airport | null>} Returns origin and destination airports of the flight.
	 */
	getAirports(aircraftLocation) {
		const aircraft = FlightPlan.getByFlightId(this.id)?.getAssignedAircraft();
		if (!aircraftLocation) {
			aircraftLocation = aircraft.location || aircraft.departedFrom;
		}

		if (!aircraftLocation) {
			return [null, null];
		}

		const airportList = store.getState().airports;
		const slot = Slot.get(this.#slotId).toObject();

		let origin = getAirportById(airportList, this.#origin);
		let destination = getAirportById(airportList, slot.destination);

		if (aircraftLocation === slot.destination) [origin, destination] = [destination, origin];
		if (aircraftLocation !== this.#origin && aircraftLocation !== slot.destination && aircraftLocation !== "") {
			return [null, null];
		}

		return [origin, destination];
	}

	/**
	 * A method to get the codes of the origin and destination airport.
	 * @param {Boolean} prefersICAO Weather the user prefers ICAO over IATA.
	 */
	getCodes(prefersICAO = false) {
		const [origin, destination] = this.getAirports();
		if (!origin || !destination) return [null, null];
		if (prefersICAO) return [origin.ident || origin.iata_code, origin.ident || destination.iata_code];
		return [origin.iata_code || origin.ident, destination.iata_code || origin.ident];
	}

	getStaticCodes(prefersICAO = false) {
		const airportList = store.getState().airports;
		const slot = Slot.get(this.#slotId).toObject();

		let origin = getAirportById(airportList, this.#origin);
		let destination = getAirportById(airportList, slot.destination);

		if (!origin || !destination) return [null, null];
		if (prefersICAO) return [origin.ident || origin.iata_code, origin.ident || destination.iata_code];
		return [origin.iata_code || origin.ident, destination.iata_code || origin.ident];
	}

	/**
	 * A method that validates and sets a new flight number.
	 * @param {String} number The new flight number.
	 * @return {Promise<Boolean>} Success.
	 */
	async setNumber(number) {
		if (number === this.number) return true;

		const lang = store.getState().lang;

		try {
			await axios.patch(`${api}/flight/${this.id}/number`, { number });
			this.#number = number;
		} catch (error) {
			if (error.response.status === 400) {
				alert(
					lang === "PL"
						? "Numer lotu musi mieć format: XX 001"
						: "The flight number must have the following format: XX 001"
				);
				return false;
			}

			if (error.response.status === 422) {
				alert(
					lang === "PL"
						? `Lot o numerze '${number}' już istnieje!`
						: `A flight with same number ('${number}') already exists!`
				);
				return false;
			}

			alert(lang === "PL" ? `Wystąpił błąd!` : `An error ocurred!`);

			console.error(error);
			return false;
		}

		return true;
	}

	/**
	 * A static method that gets a flight with provided id
	 * @param {Number} id Flight id.
	 * @return {Flight | null} Returns the flight or undefined.
	 */
	static get(id) {
		return Flight.getAll().find(flight => flight.id === id);
	}

	/**
	 * Returns all flights saved in Redux
	 * @return {Array<Flight>}
	 */
	static getAll() {
		return store.getState().flights;
	}

	/**
	 * A static method that gets all flights assigned to a flight plan of provided id
	 * @param {Array<Number>} id Flight ids.
	 * @return {Array<Flight>} Returns an array of flights.
	 */
	static getByIds(flightIds) {
		if (!Array.isArray(flightIds)) return [];
		return Flight.getAll().filter(flight => flightIds.includes(flight.id));
	}

	/**
	 * A function that returns a flight with provided sequence from a provided flight plan.
	 * @param {Array<Number>} flightIds Haystack
	 * @param {Number} sequence Flight sequence
	 */
	static getBySequence(flightIds, sequence) {
		const allFlights = Flight.getByIds(flightIds);
		return allFlights.find(flight => flight.sequence === sequence);
	}

	/**
	 * A method that checks if flight with same number already exists.
	 * @param {String | Number} number Flight number.
	 * @param {Number | Array<Number>} ids An id of the flight to ignore (or an array of ids).
	 * @return {Boolean} Returns true if flight with provided flight number exists.
	 */
	static exists(number, ids, notSavedNumbers = []) {
		notSavedNumbers = notSavedNumbers.map(n => +n.replace(/\D/g, ""));
		typeof number === "string" && (number = +number.replace(/\D/g, ""));
		let allFlights = Flight.getAll();

		if (typeof ids === "number") ids = [ids];
		if (Array.isArray(ids)) allFlights = allFlights.filter(f => !ids.includes(f.id));

		const numbers = [...allFlights.map(f => +f.number.replace(/\D/g, "")), ...notSavedNumbers];
		return numbers.includes(number);
	}

	static getColor(otherFlights) {
		const colors = ["#794F92", "#EB5C68", "#F9DD62", "#4994FD", "#00B8A2", "#6CAE32"];
		const usedColors = otherFlights.map(f => f.color);
		const filteredColors = colors.filter(c => !usedColors.includes(c));
		return filteredColors[0] || null;
	}

	static getMaxRange(flights) {
		return flights.reduce(
			(biggestValue, flight) => (flight.distance > biggestValue ? flight.distance : biggestValue),
			0
		);
	}

	// Object conversions
	/**
	 * A method used to convert a class instance into an object. Also outputs private properties!
	 * @return {Object} A class instance converted to an object
	 */
	toObject() {
		return {
			id: this.#id,
			slotId: this.#slotId,
			origin: this.#origin,
			number: this.#number,
			prices: this.prices,
			distance: this.#distance,
			color: this.#color,
			sequence: this.sequence
		};
	}

	/**
	 * A static method used to convert an object to a Flight
	 * @param {Object} obj The object to convert
	 * @static
	 */
	static fromObject({ slotId, origin, color, sequence, ...rest }) {
		return new Flight(slotId, origin, color, sequence, rest);
	}
}

export default Flight;
